import React, { useEffect, useRef } from "react";

/**
 * Subtle background bubbles with responsive/mobile support
 *
 * Props (same names as your version, all optional):
 *  - density (0.2..1.2)
 *  - maxRadius (10..52)
 *  - speed (0.5..1.6)
 *  - hueShift (-20..20)
 *  - opacity (0..1)
 *  - sparkle (0..1)
 *  - influenceRadius (px)
 *  - influenceStrength (0..2)
 *  - style (object)
 */
export default function BubbleFX({
  density = 0.45,
  maxRadius = 40,
  speed = 0.9,
  hueShift = 0,
  opacity = 0.25,
  sparkle = 0.2,
  influenceRadius = 140,
  influenceStrength = 0.65,
  style = {},
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const runningRef = useRef(true); // paused on hidden/offscreen

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });

    // Respect reduced motion
    const mReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const reducedMotion = !!mReduced.matches;

    // DPR (cap to 2 for performance)
    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    let w = 0, h = 0;

    // Random
    const rand = (() => { let s = 0x12345678;
      return () => { s^=s<<13; s^=s>>>17; s^=s<<5; return (s>>>0)/0xFFFFFFFF; };
    })();

    // Resize observer + DPR change handling
    function resize() {
      const r = wrap.getBoundingClientRect();
      w = Math.max(1, r.width | 0);
      h = Math.max(1, r.height | 0);

      // Re-read DPR (iOS Safari can change on zoom/orientation)
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

      canvas.width = (w * dpr) | 0;
      canvas.height = (h * dpr) | 0;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Rebuild population when size changes a lot
      resetPopulation();
    }
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    // Visibility / Intersection: pause when not visible
    const visHandler = () => (runningRef.current = document.visibilityState === "visible");
    document.addEventListener("visibilitychange", visHandler);

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        runningRef.current = e?.isIntersecting !== false && document.visibilityState === "visible";
      },
      { root: null, threshold: 0.01 }
    );
    io.observe(wrap);

    // ==== Population / bubbles ====
    let bubbles = [];
    let pops = [];

    function targetCounts() {
      // Adaptive based on area + device class
      // Smaller screens & high DPR => fewer bubbles
      const area = w * h;
      const base = ((area / 32000) * density);

      const isSmall = w <= 480 || h <= 560;
      const isTablet = !isSmall && (w <= 1180 || h <= 900);

      let count = base;
      if (isSmall) count *= 0.6;          // calm on phones
      else if (isTablet) count *= 0.85;   // moderate on tablets

      // Clamp overall
      const min = isSmall ? 6 : 8;
      const max = isTablet ? 38 : 45;
      return Math.max(min, Math.min(max, Math.round(count)));
    }

    function radiusForDevice() {
      const isSmall = w <= 480 || h <= 560;
      const isTablet = !isSmall && (w <= 1180 || h <= 900);
      let r = maxRadius;
      if (isSmall) r = Math.min(r, 28);
      else if (isTablet) r = Math.min(r, 34);
      return r;
    }

    function newBubble(fromBottom = true) {
      const rMax = radiusForDevice();
      const r = 8 + rand() * (rMax - 8);
      const x = rand() * w;
      const y = fromBottom ? h + r + rand() * h * 0.2 : rand() * h;

      // Motion scaled by speed + reduced motion
      const spd = reducedMotion ? speed * 0.6 : speed;
      const vy = (-14 - rand() * 18) / 60 * spd; // rise
      const vx = (-0.18 + rand() * 0.36) * (0.5 + (r / rMax) * 0.6); // gentle drift
      return { x, y, r, vy, vx };
    }

    function resetPopulation() {
      const COUNT = targetCounts();
      // preserve some existing bubbles on minor resizes for continuity
      if (bubbles.length === COUNT) return;
      if (bubbles.length > COUNT) {
        bubbles.length = COUNT;
      } else {
        const needed = COUNT - bubbles.length;
        for (let i = 0; i < needed; i++) bubbles.push(newBubble(false));
      }
    }

    // Initial size & population
    resize();

    // ==== Pops / effects ====
    function addPop(x, y = h * 0.08, power = 1) {
      pops.push({ type: "ring", x, y, life: 0, maxLife: 46 + rand() * 12, power });
      const sIntensity = (reducedMotion ? 0 : sparkle) * power;
      const n = Math.round(3 + sIntensity * 6);
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / Math.max(1, n) + rand() * 0.5;
        const v = (0.5 + rand() * 1.2) * (0.6 + sIntensity);
        pops.push({
          type: "spark", x, y,
          vx: Math.cos(a) * v, vy: Math.sin(a) * v * 0.35,
          life: 0, maxLife: 36 + rand() * 14
        });
      }
    }

    function drawBubble(b) {
      const { x, y, r } = b;

      const g = ctx.createRadialGradient(
        x - r * 0.35, y - r * 0.35, r * 0.1,
        x, y, r * 1.05
      );
      const tint1 = `hsla(${200 + hueShift}, 100%, 98%, 0.75)`;
      const tint2 = `hsla(${188 + hueShift}, 75%, 85%, 0.16)`;
      const tint3 = `hsla(${215 + hueShift}, 70%, 80%, 0.08)`;
      g.addColorStop(0, tint1);
      g.addColorStop(0.45, "rgba(255,255,255,0.24)");
      g.addColorStop(0.75, tint2);
      g.addColorStop(0.95, tint3);
      g.addColorStop(1, "rgba(255,255,255,0.0)");

      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();

      // subtle rim
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = Math.max(0.4, r * 0.045);
      ctx.beginPath();
      ctx.arc(x, y, r * 0.98, 0, Math.PI * 2);
      ctx.stroke();

      // tiny highlight
      ctx.fillStyle = "rgba(255,255,255,0.48)";
      ctx.beginPath();
      ctx.ellipse(x - r * 0.33, y - r * 0.36, r * 0.17, r * 0.11, -0.6, 0, Math.PI * 2);
      ctx.fill();
    }

    function drawPop(p) {
      if (p.type === "ring") {
        const t = p.life / p.maxLife;
        const radius = 3 + t * (22 + 8 * p.power);
        ctx.strokeStyle = `rgba(255,255,255,${0.3 * (1 - t)})`;
        ctx.lineWidth = 1.1 * (1 - t);
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        const t = p.life / p.maxLife;
        ctx.globalAlpha = 0.7 * (1 - t);
        ctx.fillStyle = "rgba(255,255,255,0.8)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.1, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // ===== Input (window-based so the canvas won't block clicks) =====
    const mouse = { x: 0, y: 0, active: false, power: 0 };

    function updatePointerFromEvent(e) {
      const rect = wrap.getBoundingClientRect();
      let clientX, clientY;
      if (e.touches && e.touches.length) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      mouse.x = clientX - rect.left;
      mouse.y = clientY - rect.top;
    }

    const onPointerMove = (e) => {
      updatePointerFromEvent(e);
      mouse.active = true;
      mouse.power = Math.min(1, mouse.power + 0.12); // ease in
    };

    const onPointerEnd = () => {
      mouse.active = false; // ease out in loop
    };

    const onPointerDown = (e) => {
      updatePointerFromEvent(e);
      // gentle pop on tap/click
      addPop(mouse.x, mouse.y, 1.0);
      mouse.active = true;
      mouse.power = 0.9;
    };

    // Passive listeners on window (so the canvas layer doesn't intercept clicks)
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointerup", onPointerEnd, { passive: true });
    window.addEventListener("pointercancel", onPointerEnd, { passive: true });
    window.addEventListener("touchmove", onPointerMove, { passive: true });
    window.addEventListener("touchend", onPointerEnd, { passive: true });
    window.addEventListener("touchcancel", onPointerEnd, { passive: true });

    // ==== LOOP ====
    let last = performance.now();
    function frame(now) {
      rafRef.current = requestAnimationFrame(frame);
      if (!runningRef.current) return; // paused/offscreen/hidden

      const dt = Math.min(40, now - last);
      last = now;

      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, opacity));

      // faint vertical lift
      const grad = ctx.createLinearGradient(0, h, 0, 0);
      grad.addColorStop(0, "rgba(255,255,255,0.015)");
      grad.addColorStop(1, "rgba(255,255,255,0.05)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // ease out mouse power when inactive
      if (!mouse.active && mouse.power > 0) {
        mouse.power = Math.max(0, mouse.power - 0.03);
      }

      const r2 = influenceRadius * influenceRadius;

      // bubbles
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];

        // base motion (calmer if reduced motion)
        const timeScale = dt / 16.7 * (reducedMotion ? 0.8 : 1);
        b.x += b.vx * timeScale;
        b.y += b.vy * timeScale;

        // mouse/touch push
        if (mouse.power > 0) {
          const dx = b.x - mouse.x;
          const dy = b.y - mouse.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < r2) {
            const dist = Math.max(1, Math.sqrt(dist2));
            const fall = (1 - dist2 / r2);
            const f = influenceStrength * mouse.power * fall;
            b.x += (dx / dist) * f * timeScale * 2.2;
            b.y += (dy / dist) * f * timeScale * 2.2;
          }
        }

        // wrap sides
        if (b.x < -b.r) b.x = w + b.r;
        if (b.x > w + b.r) b.x = -b.r;

        drawBubble(b);

        // auto pop near top
        if (b.y < h * 0.08 + b.r * 0.2) {
          addPop(b.x, b.y, 1.0);
          bubbles[i] = newBubble(true);
        }
      }

      // pops
      for (let i = pops.length - 1; i >= 0; i--) {
        const p = pops[i];
        p.life += dt * 0.06 * (reducedMotion ? 0.7 : 1) * speed;
        if (p.type === "spark") {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.012;
        }
        drawPop(p);
        if (p.life >= p.maxLife) pops.splice(i, 1);
      }

      ctx.restore();
    }
    rafRef.current = requestAnimationFrame(frame);

    // Cleanup
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", visHandler);

      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerEnd);
      window.removeEventListener("touchcancel", onPointerEnd);
    };
  }, [
    density, maxRadius, speed, hueShift, opacity, sparkle,
    influenceRadius, influenceStrength
  ]);

  return (
    <div
      ref={wrapRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,           // stays behind house/car/text
        pointerEvents: "none", // DO NOT block hero buttons/links
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </div>
  );
}
